import { storableError } from '../util/errors';
import { getAllUsernames, getArtistsWithListings } from '../util/api';

// ================ Action types ================ //

export const FETCH_ARTIST_USERNAMES_REQUEST = 'app/artistUsernames/FETCH_ARTIST_USERNAMES_REQUEST';
export const FETCH_ARTIST_USERNAMES_SUCCESS = 'app/artistUsernames/FETCH_ARTIST_USERNAMES_SUCCESS';
export const FETCH_ARTIST_USERNAMES_ERROR = 'app/artistUsernames/FETCH_ARTIST_USERNAMES_ERROR';

export const FETCH_ARTISTS_WITH_LISTINGS_REQUEST = 'app/artistUsernames/FETCH_ARTISTS_WITH_LISTINGS_REQUEST';
export const FETCH_ARTISTS_WITH_LISTINGS_SUCCESS = 'app/artistUsernames/FETCH_ARTISTS_WITH_LISTINGS_SUCCESS';
export const FETCH_ARTISTS_WITH_LISTINGS_ERROR = 'app/artistUsernames/FETCH_ARTISTS_WITH_LISTINGS_ERROR';

// ================ Reducer ================ //

const initialState = {
  // Raw data from the API (object with userId keys)
  usernamesData: {},
  // Processed array of artists for directory
  artists: [],
  // Username to ID mapping for profile lookups
  usernameToIdMapping: {},
  // Loading and error states
  fetchInProgress: false,
  fetchError: null,
  // Timestamp for cache validation
  lastFetched: null,
  // Artists with their listing images - simplified
  artistsWithListings: null,
  artistImages: null,
  fetchArtistsWithListingsInProgress: false,
  fetchArtistsWithListingsError: null,
};

export default function artistUsernamesReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_ARTIST_USERNAMES_REQUEST:
      return {
        ...state,
        fetchInProgress: true,
        fetchError: null,
      };
    case FETCH_ARTIST_USERNAMES_SUCCESS:
      const { data: usernamesData, timestamp } = payload;
      
      // Create username to ID mapping for profile page lookups
      const usernameToIdMapping = Object.entries(usernamesData).reduce((mapping, [userId, userData]) => {
        if (userData.username) {
          mapping[userData.username.toLowerCase()] = userId;
        }
        return mapping;
      }, {});
      
      // Create processed artists array for directory page
      const artists = Object.entries(usernamesData)
        .map(([userId, userData]) => userData)
        .filter(artist => artist.hasPostListingsPermission && artist.username)
        .sort((a, b) => {
          const nameA = a.name || '';
          const nameB = b.name || '';
          return nameA.toLowerCase().localeCompare(nameB.toLowerCase());
        });
      
      return {
        ...state,
        usernamesData,
        artists,
        usernameToIdMapping,
        fetchInProgress: false,
        lastFetched: timestamp,
      };
    case FETCH_ARTIST_USERNAMES_ERROR:
      return {
        ...state,
        fetchInProgress: false,
        fetchError: payload,
      };
    case FETCH_ARTISTS_WITH_LISTINGS_REQUEST:
      return {
        ...state,
        fetchArtistsWithListingsInProgress: true,
        fetchArtistsWithListingsError: null,
      };
    case FETCH_ARTISTS_WITH_LISTINGS_SUCCESS:
      const { artists: artistsData, artistImages } = payload;
      return {
        ...state,
        artistsWithListings: artistsData,
        artistImages: artistImages,
        fetchArtistsWithListingsInProgress: false,
      };
    case FETCH_ARTISTS_WITH_LISTINGS_ERROR:
      return {
        ...state,
        fetchArtistsWithListingsInProgress: false,
        fetchArtistsWithListingsError: payload,
      };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const fetchArtistUsernamesRequest = () => ({ type: FETCH_ARTIST_USERNAMES_REQUEST });
export const fetchArtistUsernamesSuccess = (data, timestamp) => ({
  type: FETCH_ARTIST_USERNAMES_SUCCESS,
  payload: { data, timestamp },
});
export const fetchArtistUsernamesError = (error) => ({
  type: FETCH_ARTIST_USERNAMES_ERROR,
  payload: error,
});

export const fetchArtistsWithListingsRequest = () => ({ type: FETCH_ARTISTS_WITH_LISTINGS_REQUEST });
export const fetchArtistsWithListingsSuccess = (artists, artistImages) => ({
  type: FETCH_ARTISTS_WITH_LISTINGS_SUCCESS,
  payload: { artists, artistImages },
});
export const fetchArtistsWithListingsError = (error) => ({
  type: FETCH_ARTISTS_WITH_LISTINGS_ERROR,
  payload: error,
});

// ================ Selectors ================ //

export const getArtists = (state) => state.artistUsernames.artists;
export const getUsernameToIdMapping = (state) => state.artistUsernames.usernameToIdMapping;
export const getArtistUsernamesData = (state) => state.artistUsernames.usernamesData;
export const isArtistUsernamesFetchInProgress = (state) => state.artistUsernames.fetchInProgress;
export const getArtistUsernamesError = (state) => state.artistUsernames.fetchError;
export const getArtistUsernamesLastFetched = (state) => state.artistUsernames.lastFetched;

export const getArtistsWithListingsData = (state) => state.artistUsernames.artistsWithListings;
export const getArtistImages = (state) => state.artistUsernames.artistImages;
export const isArtistsWithListingsFetchInProgress = (state) => state.artistUsernames.fetchArtistsWithListingsInProgress;
export const getArtistsWithListingsError = (state) => state.artistUsernames.fetchArtistsWithListingsError;

// Cache TTL: 15 minutes
const CACHE_TTL = 15 * 60 * 1000;

export const isArtistUsernamesCacheValid = (state) => {
  const lastFetched = getArtistUsernamesLastFetched(state);
  if (!lastFetched) return false;
  
  const now = Date.now();
  const cacheAge = now - lastFetched;
  return cacheAge < CACHE_TTL;
};



// ================ Thunks ================ //

export const fetchArtistUsernames = (forceRefresh = false) => (dispatch, getState) => {
  const state = getState();
  
  // Check if we need to fetch (not in progress, cache invalid, or force refresh)
  const inProgress = isArtistUsernamesFetchInProgress(state);
  const cacheValid = isArtistUsernamesCacheValid(state);
  
  if (inProgress) {
    return Promise.resolve();
  }
  
  if (!forceRefresh && cacheValid) {
    return Promise.resolve();
  }
  
  dispatch(fetchArtistUsernamesRequest());
  
  return getAllUsernames({ 
    includeProfile: true, 
    userType: 'artist' 
  })
    .then(response => {
      if (!response || !response.data) {
        throw new Error('Failed to fetch artist usernames');
      }
      
      const timestamp = Date.now();
      dispatch(fetchArtistUsernamesSuccess(response.data, timestamp));
      
      return response.data;
    })
    .catch(error => {
      dispatch(fetchArtistUsernamesError(storableError(error)));
      throw error;
    });
};

export const fetchArtistsWithListings = () => (dispatch, getState) => {
  const state = getState();
  
  // // Don't fetch if already in progress
  // const inProgress = isArtistsWithListingsFetchInProgress(state);
  // if (inProgress) {
  //   return Promise.resolve();
  // }
  
  // // Don't fetch if we already have data
  // const existingData = getArtistsWithListingsData(state);
  // if (existingData) {
  //   return Promise.resolve();
  // }
  
  dispatch(fetchArtistsWithListingsRequest());
  
  return getArtistsWithListings()
    .then(response => {
      if (!response || !response.data) {
        throw new Error('Failed to fetch artists with listings');
      }
      
      const { artists, artistImages } = response.data;
      dispatch(fetchArtistsWithListingsSuccess(artists, artistImages));
      
      return response.data;
    })
    .catch(error => {
      dispatch(fetchArtistsWithListingsError(storableError(error)));
      throw error;
    });
};

// Helper function to get user ID by username (case-insensitive)
export const getUserIdByUsername = (state, username) => {
  const mapping = getUsernameToIdMapping(state);
  return mapping[username.toLowerCase()] || null;
}; 